import React from 'react';
import { motion } from 'framer-motion';
import { Shield, FileText, AlertTriangle, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';

const TermsOfService: React.FC = () => {
  const sections = [
    {
      title: "1. Service Scope",
      content: [
        "Aedon Community is a platform providing FiveM servers, Discord bot and related services.",
        "Our Discord bot provides server management, moderation and entertainment features.",
        "Our website provides server information, scripts and sponsored content.",
        "All our services are provided in compliance with applicable laws and regulations."
      ]
    },
    {
      title: "2. Terms of Use",
      content: [
        "By using our services, you agree to these terms.",
        "Parental consent is required for users under 18 years of age.",
        "You must use our services for legal purposes only.",
        "You may not engage in behavior that violates the rights of others.",
        "Spam, trolling and offensive content sharing is prohibited."
      ]
    },
    {
      title: "3. Discord Bot Usage",
      content: [
        "By using our Discord bot, you also agree to Discord's own terms.",
        "Misusing bot commands is prohibited.",
        "Spamming through the bot or harassing other users is prohibited.",
        "Attempting to bypass the bot's security features is prohibited.",
        "Manipulating or deleting bot logs is prohibited."
      ]
    },
    {
      title: "4. FiveM Server Rules",
      content: [
        "You must follow community rules when playing on our servers.",
        "Using cheats or exploits is strictly prohibited.",
        "Harassing or insulting other players is prohibited.",
        "Behaviors affecting server performance are prohibited.",
        "You must comply with server administrators' decisions."
      ]
    },
    {
      title: "5. Sponsored Content and Advertisements",
      content: [
        "Our website contains sponsored content and advertisements.",
        "Betting and casino platforms such as MrGamb are among our sponsors.",
        "Using these sponsors' services is entirely your choice.",
        "Aedon Community is not responsible for issues arising from sponsor services.",
        "Users under 18 years of age are prohibited from using betting sites.",
        "Gambling games can be addictive, please play responsibly.",
        "By clicking sponsor links, you agree to these platforms' own terms.",
        "Users from regions where gambling is illegal are responsible for their own actions.",
        "We do not accept responsibility for users from gambling-prohibited regions.",
        "In case of complaints from prohibited regions, we do not accept any liability."
      ]
    },
    {
      title: "6. Intellectual Property Rights",
      content: [
        "Aedon Community logo, design and content are copyright protected.",
        "Copying or distributing our content without permission is prohibited.",
        "Discord bot codes and website codes are our private property.",
        "You need permission to use our scripts for commercial purposes.",
        "Using the Aedon Community brand without permission is prohibited."
      ]
    },
    {
      title: "7. Disclaimer of Liability",
      content: [
        "We provide our services 'as is' without any warranties.",
        "We are not responsible for data loss or system failures.",
        "We are not responsible for issues arising from third-party services.",
        "We are not responsible for problems arising from users' own devices.",
        "We are not responsible for financial losses from sponsor services.",
        "We are not responsible for users from regions where gambling is illegal."
      ]
    },
    {
      title: "8. Account Suspension and Termination",
      content: [
        "We may suspend or close accounts that violate rules.",
        "Spam, cheating, harassment are grounds for permanent ban.",
        "We may make our decisions without explanation.",
        "Banned accounts are not guaranteed to be reopened.",
        "Ban evasion is prohibited and may result in additional penalties."
      ]
    },
    {
      title: "9. Changes",
      content: [
        "We may change these terms without prior notice.",
        "Changes will be published on our website.",
        "Service usage after changes means acceptance of new terms.",
        "Users who do not accept changes should stop using our services."
      ]
    },
    {
      title: "10. Contact",
      content: [
        "For questions: info@aedoncommunity.com",
        "Discord: https://discord.gg/aedonv",
        "Website: https://aedoncommunity.com",
        "You can submit complaints and suggestions through these channels."
      ]
    }
  ];

  return (
    <>
      <SEO 
        title="Terms of Service - Aedon Community"
        description="Terms of service for Aedon Community and Discord bot. Detailed information about FiveM servers, sponsored content and community rules."
        keywords="Aedon Community terms of service, Discord bot rules, FiveM server rules, sponsored content, gambling site sponsorship"
        url="/terms-of-service"
      />
      <div className="min-h-screen pt-20 bg-dark-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center mb-6">
              <FileText className="w-12 h-12 text-primary-400 mr-4" />
              <h1 className="text-4xl md:text-5xl font-gaming font-bold text-white">
                Terms of Service
              </h1>
            </div>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Terms of service for Aedon Community and Discord bot
            </p>
            <div className="mt-6 flex items-center justify-center space-x-4 text-sm text-gray-500">
              <span>Last updated: December 22, 2024</span>
              <span>•</span>
              <span>Version: 1.0</span>
            </div>
          </motion.div>

          {/* Warning Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-6 mb-8"
          >
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-6 h-6 text-yellow-400 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-yellow-400 mb-2">
                  Important Warning
                </h3>
                <p className="text-gray-300">
                  By using these services, you agree to the terms below. 
                  Please read the sections about sponsored content and gambling sites carefully.
                  Users from regions where gambling is illegal are responsible for their own actions.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Terms Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                className="card"
              >
                <h2 className="text-2xl font-gaming font-bold text-white mb-6 flex items-center">
                  <Shield className="w-6 h-6 text-primary-400 mr-3" />
                  {section.title}
                </h2>
                <div className="space-y-4">
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                      <p className="text-gray-300 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-12 text-center"
          >
            <div className="bg-dark-800/50 rounded-lg p-6 border border-dark-700">
              <p className="text-gray-400 mb-4">
                Users who do not accept these terms should stop using our services.
              </p>
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                <span>© 2024 Aedon Community</span>
                <span>•</span>
                <span>All rights reserved</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default TermsOfService; 